// DevItemDetailScreen - §12 개발품 상세.
//
// **담당자의 유일한 입력 화면**이다. §17 STEP 2~4 가 전부 여기서 일어나고,
// §2 는 "1건당 1분 이내" 를 요구한다. 그래서
//   - 완료 처리는 클릭 1회다. 확인 다이얼로그가 없고 5초 되돌리기 토스트가
//     대신 있다 (결정 S-10). 모달은 클릭을 하나 더 만든다.
//   - 계획일은 저장 버튼 없이 `onBlur` 즉시 PATCH 다 (결정 S-9).
//   - 완료 API 는 본문이 없다. 완료일은 서버가 KST 로 찍는다 (§05 A-4) -
//     클라이언트가 날짜를 만들면 타임존 버그가 들어온다.
//   - 화면은 `lib/optimistic.ts` 로 파생값을 즉시 다시 계산하고, 실패하면
//     이전 스냅샷으로 되돌린 뒤 오류 토스트를 띄운다.
//
// 탭이 없다. §12 는 "한 화면에서 확인" 을 요구하므로 7개 블록이 모두 보인다.
import { DevItemForm } from "@/components/DevItemForm";
import { IssueForm, type IssueTarget } from "@/components/IssueForm";
import { MilestoneTrack } from "@/components/MilestoneTrack";
import { StatusBadge } from "@/components/StatusIndicator";
import { THead, Table, TableScroll, Td, Th, Tr } from "@/components/Table";
import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DDayChip,
  DataBoundary,
  DateField,
  Dialog,
  Field,
  Icon,
  Num,
  ProgressBar,
  Select,
  Skeleton,
  TextInput,
  Tooltip,
} from "@/components/ui";
import {
  type Customer,
  type DevItemDetail,
  type Issue,
  type IssueTargetType,
  type ProcessMaster,
  type ProductionPlan,
  type Staff,
  autoScheduleDevItem,
  completeMilestone,
  completePrepItem,
  completeProductionPlan,
  createPrepItem,
  createProductionPlan,
  deleteDevItem,
  deleteDrawing,
  deletePrepItem,
  deleteProductionPlan,
  getDrawingUrl,
  putRoute,
  uncompleteMilestone,
  updateDevItem,
  updateMilestone,
  updatePrepItem,
  updateProductionPlan,
  uploadDrawing,
  DRAWING_EXTENSIONS,
  MAX_DRAWINGS_PER_ITEM,
  MAX_DRAWING_BYTES,
} from "@/lib/api";
import { asOfLabel, dotDate, monthDay } from "@/lib/dates";
import { EMPTY } from "@/lib/format";
import { useDevItem } from "@/lib/hooks";
import { withMilestone, withPrepItem, withProductionPlan } from "@/lib/optimistic";
import { PREP_DEFS, statusKey } from "@/lib/status";
import type { ScreenNav } from "@/screens/nav";
import { useEffect, useRef, useState } from "react";

const fileSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)}MB`
    : `${Math.max(1, Math.round(bytes / 1024))}KB`;

/**
 * 마일스톤 id 는 `{itemId}:{key}`, 준비항목 id 는 `{itemId}:{kind}` 다.
 * 둘 다 `material` 을 가지므로 **id 만으로 이슈를 찾으면 서로 섞인다.**
 * 항상 target_type 을 함께 본다.
 */
const findIssue = (
  issues: readonly Issue[],
  type: IssueTargetType,
  targetId: string
): Issue | undefined => issues.find((i) => i.targetType === type && i.targetId === targetId);

export const DevItemDetailScreen = ({
  itemId,
  nav,
  onBack,
  customers,
  staff,
  processes,
  drawingsEnabled,
}: {
  itemId: string;
  nav: ScreenNav;
  onBack: () => void;
  customers: readonly Customer[];
  staff: readonly Staff[];
  processes: readonly ProcessMaster[];
  /** S3 미설정이면 도면 영역만 비활성 (§05 §4-7) */
  drawingsEnabled: boolean;
}) => {
  const res = useDevItem(itemId);
  const [item, setItem] = useState<DevItemDetail | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [issueTarget, setIssueTarget] = useState<IssueTarget | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [prepOpen, setPrepOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [planEdit, setPlanEdit] = useState<ProductionPlan | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setItem(res.data);
  }, [res.data]);

  // 렌더마다 최신 item 을 담아 둔다. 큐에 들어간 호출이 실행될 시점의
  // 상태를 봐야 하므로 렌더 클로저의 `item` 을 스냅샷으로 쓸 수 없다.
  const latest = useRef<DevItemDetail | null>(null);
  latest.current = item;
  // 편집을 직렬화한다. 두 편집이 겹치면 첫 번째의 실패 롤백이 두 번째까지
  // 되돌린다 - 스냅샷이 "그 호출 직전" 이 아니라 "두 편집 전" 이 되기 때문이다.
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  /** 낙관적 갱신 후 서버 호출. 실패하면 그 호출 직전 상태로만 되돌린다. */
  const optimistic = (next: DevItemDetail, call: () => Promise<unknown>, after?: () => void) => {
    const run = async () => {
      const snapshot = latest.current;
      setItem(next);
      try {
        await call();
        after?.();
      } catch (err) {
        setItem(snapshot);
        nav.toast(
          err instanceof Error ? err.message : "저장하지 못했어요. 잠시 후 다시 시도해 주세요.",
          "error"
        );
      }
    };
    queue.current = queue.current.then(run, run);
    return queue.current;
  };

  const reload = () => void res.reload();
  // 404 는 "다시 시도"할 오류가 아니다 - 삭제된 개발품을 가리키는 오래된 링크
  // (`/items/<id>`)라서 아래 "찾을 수 없음" 카드로 목록에 돌려보낸다.
  const notFound = (res.error as { status?: unknown } | null)?.status === 404;

  return (
    <DataBoundary
      loading={res.loading}
      error={notFound ? null : res.error}
      onRetry={reload}
      skeleton={<DetailSkeleton />}
    >
      {!item ? (
        <Card>
          <p className="text-heading">개발품을 찾을 수 없습니다</p>
          <p className="mt-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
            삭제되었거나 접근 권한이 없는 개발품이에요.
          </p>
          <div className="mt-s4">
            <Button variant="secondary" size="md" onClick={onBack}>
              목록으로 돌아가기
            </Button>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-s5">
          {/* ── 헤더 ─────────────────────────────────────────────── */}
          <header className="flex flex-col gap-s3">
            <div className="flex flex-wrap items-center justify-between gap-s3">
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="chevL" size={14} />}
                onClick={onBack}
              >
                목록으로 돌아가기
              </Button>
              <div className="flex flex-wrap items-center gap-s2">
                {item.sopDate ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          const r = await autoScheduleDevItem(item.id, "fill_empty");
                          setItem(r.item);
                          nav.toast(
                            r.adjustments.length > 0
                              ? `주요 일정 ${r.applied}건을 만들었어요. ${r.adjustments.length}건은 영업일로 조정했어요.`
                              : `주요 일정 ${r.applied}건을 만들었어요.`
                          );
                        } catch (err) {
                          nav.toast(
                            err instanceof Error ? err.message : "저장하지 못했어요.",
                            "error"
                          );
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      일정 자동생성
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setRegenOpen(true)}>
                      일정 재생성
                    </Button>
                  </>
                ) : (
                  <Tooltip content="SOP 를 등록하면 표준 일정을 자동으로 만들 수 있어요.">
                    <Button variant="secondary" size="sm" disabled>
                      일정 자동생성
                    </Button>
                  </Tooltip>
                )}
                <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                  수정
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    const archived = !item.archived;
                    try {
                      setItem(await updateDevItem(item.id, { archived }));
                      nav.toastUndo(
                        archived ? "완료 처리했어요." : "진행 목록으로 되돌렸어요.",
                        async () => {
                          setItem(await updateDevItem(item.id, { archived: !archived }));
                        }
                      );
                    } catch (err) {
                      nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
                    }
                  }}
                >
                  {item.archived ? "종료 취소" : "종료"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Icon name="trash" size={14} />}
                  onClick={() => setDeleteOpen(true)}
                >
                  삭제하기
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-s1">
              <h1 className="text-display">{item.partName}</h1>
              <p className="text-label" style={{ color: "var(--text-tertiary)" }}>
                {[item.customerName, item.staffName, `품번 ${item.partNo}`, item.projectName]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <p
                className="text-caption tabular-nums"
                data-num=""
                style={{ color: "var(--text-tertiary)" }}
              >
                {asOfLabel(item.asOf)}
                {item.annualQty > 0 && (
                  <>
                    {" · 연간수량 "}
                    <Num v={item.annualQty} suffix=" EA" />
                  </>
                )}
                {item.kickoffDate && ` · 착수 ${dotDate(item.kickoffDate)}`}
              </p>
            </div>
          </header>

          {/* ── 3분할 요약 ───────────────────────────────────────── */}
          <div className="grid grid-cols-1 gap-s3 lg:grid-cols-3">
            <Card>
              <p className="text-caption" style={{ color: "var(--text-secondary)" }}>
                전체 진행률
              </p>
              <div className="mt-s2">
                <ProgressBar value={item.progress} size="hero" />
              </div>
            </Card>

            <Card>
              <p className="text-caption" style={{ color: "var(--text-secondary)" }}>
                SOP
              </p>
              <div className="mt-s2 flex flex-wrap items-center gap-s3">
                <span className="text-title tabular-nums" data-num="">
                  {dotDate(item.sopDate) || EMPTY}
                </span>
                {item.dDay !== null && <DDayChip days={item.dDay} size="hero" />}
              </div>
              <p className="mt-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
                {item.sopDate
                  ? item.sopForecast
                  : "SOP 를 등록하면 D-Day 와 자동일정을 사용할 수 있어요."}
              </p>
            </Card>

            <Card>
              <p className="text-caption" style={{ color: "var(--text-secondary)" }}>
                현재공정
              </p>
              <div className="mt-s2">
                {item.route.length === 0 ? (
                  <div className="flex flex-col gap-s2">
                    <Select
                      value=""
                      options={[]}
                      ariaLabel="현재공정"
                      placeholder="공정 Route 를 먼저 구성하세요"
                      disabled
                    />
                    <Button variant="secondary" size="sm" onClick={() => setRouteOpen(true)}>
                      Route 편집
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={item.currentProcessId}
                    ariaLabel="현재공정"
                    placeholder="선택"
                    options={item.route.map((s) => ({ value: s.processId, label: s.name }))}
                    onChange={async (v) => {
                      const snapshot = item;
                      const step = item.route.find((s) => s.processId === v);
                      setItem({
                        ...item,
                        currentProcessId: v,
                        currentProcess: step ? { id: step.processId, name: step.name } : null,
                      });
                      try {
                        setItem(await updateDevItem(item.id, { currentProcess: v }));
                        nav.toast("저장했어요.");
                      } catch (err) {
                        setItem(snapshot);
                        nav.toast(
                          err instanceof Error ? err.message : "저장하지 못했어요.",
                          "error"
                        );
                      }
                    }}
                  />
                )}
              </div>
              <p className="mt-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
                {item.nextTask
                  ? `다음 일정 ${monthDay(item.nextTask.date)} ${item.nextTask.label}`
                  : "예정된 다음 일정이 없어요."}
              </p>
            </Card>
          </div>

          {/* ── 진행 단계 (§6 9단계) ─────────────────────────────── */}
          <Card>
            <TableScroll>
              <MilestoneTrack
                size="hero"
                currentKey={item.currentStage?.key}
                items={item.milestones.map((m) => ({
                  key: m.key,
                  label: m.label,
                  status: statusKey(m.status),
                  plan: monthDay(m.plannedDate) || EMPTY,
                  actual: monthDay(m.completedDate) || EMPTY,
                }))}
              />
            </TableScroll>
          </Card>

          {/* ── 주요 이슈 ────────────────────────────────────────── */}
          <Card>
            <h2 className="text-heading">주요 이슈</h2>
            {item.issues.length === 0 ? (
              <p className="mt-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
                현재 특이사항 없음
              </p>
            ) : (
              <ul className="mt-s3 flex list-none flex-col gap-s3 p-0">
                {item.issues.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-baseline gap-s3">
                    <StatusBadge status="late" size="sm" label={`${i.delayDays}일 지연`} />
                    <span className="text-label font-semibold">{i.targetLabel}</span>
                    <span className="text-label" style={{ color: "var(--text-secondary)" }}>
                      {i.reason}
                    </span>
                    {i.action && (
                      <span className="text-caption" style={{ color: "var(--text-tertiary)" }}>
                        조치 {i.action}
                      </span>
                    )}
                    {i.expectedDate && (
                      <span
                        className="text-caption tabular-nums"
                        data-num=""
                        style={{ color: "var(--text-tertiary)" }}
                      >
                        예상 {monthDay(i.expectedDate)}
                      </span>
                    )}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() =>
                        setIssueTarget({
                          devItemId: item.id,
                          targetType: i.targetType,
                          targetId: i.targetId,
                          targetLabel: i.targetLabel,
                          issueId: i.id,
                          reason: i.reason,
                          action: i.action,
                          expectedDate: i.expectedDate,
                        })
                      }
                    >
                      수정
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* ── 주요 개발일정 (§6) ───────────────────────────────── */}
          <Card pad="sm">
            <div className="flex flex-wrap items-baseline justify-between gap-s3 px-s2 pb-s3">
              <h2 className="text-heading">주요 개발일정</h2>
              {item.milestones.every((m) => !m.plannedDate) && item.sopDate && (
                <p className="text-caption" style={{ color: "var(--text-tertiary)" }}>
                  SOP 기준 표준 일정을 자동생성할 수 있습니다.
                </p>
              )}
            </div>
            <TableScroll>
              <Table label="주요 개발일정">
                <THead sticky={false}>
                  <tr>
                    <Th width={160}>구분</Th>
                    <Th width={150}>계획일</Th>
                    <Th width={150}>완료일</Th>
                    <Th>상태</Th>
                  </tr>
                </THead>
                <tbody>
                  {item.milestones.map((m) => {
                    const issue = findIssue(item.issues, "milestone", m.id);
                    return (
                      <Tr key={m.id} tone={m.status === "red" ? "late" : "default"}>
                        <Td strong>{m.label}</Td>
                        <Td>
                          <DateField
                            ariaLabel={`${m.label} 계획일`}
                            value={m.plannedDate}
                            onChange={(v) => setItem(withMilestone(item, m.id, { plannedDate: v }))}
                            // 저장 버튼이 없다. 포커스를 잃는 순간 PATCH 한다 (결정 S-9).
                            onBlur={(v) => {
                              if (
                                v === res.data?.milestones.find((x) => x.id === m.id)?.plannedDate
                              )
                                return;
                              void optimistic(withMilestone(item, m.id, { plannedDate: v }), () =>
                                updateMilestone(m.id, { plannedDate: v })
                              );
                            }}
                          />
                        </Td>
                        <Td>
                          {/* 완료일도 편집 가능하다 (REQ-MILE-003). [완료 처리] 는
                              한 번에 오늘로 찍는 빠른 경로이고, 이 입력은 뒤늦게
                              기록하거나 날짜를 정정하는 경로다. 완료일이 계획일보다
                              이르거나 늦어도 허용된다 (REQ-MILE-009). */}
                          <DateField
                            ariaLabel={`${m.label} 완료일`}
                            value={m.completedDate}
                            onChange={(v) =>
                              setItem(withMilestone(item, m.id, { completedDate: v }))
                            }
                            onBlur={(v) => {
                              if (
                                v ===
                                res.data?.milestones.find((x) => x.id === m.id)?.completedDate
                              )
                                return;
                              void optimistic(
                                withMilestone(item, m.id, { completedDate: v }),
                                () => updateMilestone(m.id, { completedDate: v }),
                                reload
                              );
                            }}
                          />
                        </Td>
                        <Td>
                          <span className="flex flex-wrap items-center gap-s2">
                            <StatusBadge status={statusKey(m.status)} size="sm" />
                            {m.completedDate ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  void optimistic(
                                    withMilestone(item, m.id, { completedDate: "" }),
                                    () => uncompleteMilestone(m.id),
                                    reload
                                  )
                                }
                              >
                                완료취소
                              </Button>
                            ) : (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  const next = withMilestone(item, m.id, {
                                    completedDate: item.asOf,
                                  });
                                  void optimistic(
                                    next,
                                    () => completeMilestone(m.id),
                                    () => {
                                      nav.toastUndo("완료 처리했어요.", async () => {
                                        await uncompleteMilestone(m.id);
                                        reload();
                                      });
                                    }
                                  );
                                }}
                              >
                                완료 처리
                              </Button>
                            )}
                            {/* 지연 행에만 사유 입력 UI 가 나타난다 (REQ-DELAY-008). */}
                            {m.status === "red" && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() =>
                                  setIssueTarget({
                                    devItemId: item.id,
                                    targetType: "milestone",
                                    targetId: m.id,
                                    targetLabel: m.label,
                                    issueId: issue?.id ?? null,
                                    reason: issue?.reason ?? "",
                                    action: issue?.action ?? "",
                                    expectedDate: issue?.expectedDate ?? "",
                                  })
                                }
                              >
                                {issue ? "사유 수정" : "사유 입력"}
                              </Button>
                            )}
                          </span>
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableScroll>
          </Card>

          {/* ── 생산 준비현황 (§8) ───────────────────────────────── */}
          <Card pad="sm">
            <div className="flex flex-wrap items-baseline justify-between gap-s3 px-s2 pb-s3">
              <h2 className="text-heading">생산 준비현황</h2>
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="plus" size={13} />}
                onClick={() => setPrepOpen(true)}
              >
                준비항목 추가
              </Button>
            </div>
            {item.prepItems.length === 0 ? (
              <p className="px-s2 pb-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
                등록된 준비항목이 없어요.
              </p>
            ) : (
              <TableScroll>
                <Table label="생산 준비현황">
                  <THead sticky={false}>
                    <tr>
                      <Th width={110}>항목</Th>
                      <Th width={150}>필요일</Th>
                      <Th width={150}>예정일</Th>
                      <Th width={150}>완료일</Th>
                      <Th>상태</Th>
                    </tr>
                  </THead>
                  <tbody>
                    {item.prepItems.map((p) => {
                      const issue = findIssue(item.issues, "prep", p.id);
                      const server = res.data?.prepItems.find((x) => x.id === p.id);
                      return (
                        <Tr key={p.id} tone={p.status === "red" ? "late" : "default"}>
                          <Td strong>{p.label}</Td>
                          <Td>
                            <DateField
                              ariaLabel={`${p.label} 필요일`}
                              value={p.requiredDate}
                              onChange={(v) =>
                                setItem(withPrepItem(item, p.id, { requiredDate: v }))
                              }
                              onBlur={(v) => {
                                if (v === server?.requiredDate) return;
                                void optimistic(withPrepItem(item, p.id, { requiredDate: v }), () =>
                                  updatePrepItem(p.id, { requiredDate: v })
                                );
                              }}
                            />
                          </Td>
                          <Td>
                            <DateField
                              ariaLabel={`${p.label} 예정일`}
                              value={p.expectedDate}
                              onChange={(v) =>
                                setItem(withPrepItem(item, p.id, { expectedDate: v }))
                              }
                              onBlur={(v) => {
                                if (v === server?.expectedDate) return;
                                void optimistic(withPrepItem(item, p.id, { expectedDate: v }), () =>
                                  updatePrepItem(p.id, { expectedDate: v })
                                );
                              }}
                            />
                          </Td>
                          <Td>
                            <DateField
                              ariaLabel={`${p.label} 완료일`}
                              value={p.completedDate}
                              onChange={(v) =>
                                setItem(withPrepItem(item, p.id, { completedDate: v }))
                              }
                              onBlur={(v) => {
                                if (v === server?.completedDate) return;
                                void optimistic(
                                  withPrepItem(item, p.id, { completedDate: v }),
                                  () => updatePrepItem(p.id, { completedDate: v }),
                                  reload
                                );
                              }}
                            />
                          </Td>
                          <Td>
                            <span className="flex flex-wrap items-center gap-s2">
                              <StatusBadge status={statusKey(p.status)} size="sm" />
                              {p.warning && (
                                <span className="text-micro" style={{ color: "var(--state-warn)" }}>
                                  {p.warning}
                                </span>
                              )}
                              {!p.completedDate && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => {
                                    const next = withPrepItem(item, p.id, {
                                      completedDate: item.asOf,
                                    });
                                    void optimistic(
                                      next,
                                      () => completePrepItem(p.id),
                                      () => {
                                        nav.toastUndo("입고완료로 저장했어요.", async () => {
                                          await updatePrepItem(p.id, { completedDate: "" });
                                          reload();
                                        });
                                      }
                                    );
                                  }}
                                >
                                  입고완료
                                </Button>
                              )}
                              {/* 품목에 해당 없는 준비항목 제거 (REQ-PREP-003).
                                  기본 4종도 지울 수 있다 - 전조금형이 없는 품목에
                                  전조금형 행이 남아 있으면 §8 경고가 오탐이 된다. */}
                              <Button
                                variant="ghost"
                                size="sm"
                                icon={<Icon name="trash" size={12} />}
                                onClick={async () => {
                                  try {
                                    await deletePrepItem(p.id);
                                    reload();
                                    nav.toast("삭제했어요.");
                                  } catch (err) {
                                    nav.toast(
                                      err instanceof Error ? err.message : "삭제하지 못했어요.",
                                      "error"
                                    );
                                  }
                                }}
                              >
                                <span className="sr-only">{`${p.label} 삭제`}</span>
                              </Button>
                              {p.status === "red" && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() =>
                                    setIssueTarget({
                                      devItemId: item.id,
                                      targetType: "prep",
                                      targetId: p.id,
                                      targetLabel: p.label,
                                      issueId: issue?.id ?? null,
                                      reason: issue?.reason ?? "",
                                      action: issue?.action ?? "",
                                      expectedDate: issue?.expectedDate ?? "",
                                    })
                                  }
                                >
                                  {issue ? "사유 수정" : "사유 입력"}
                                </Button>
                              )}
                            </span>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableScroll>
            )}
          </Card>

          {/* ── 공정 Route (§7) ──────────────────────────────────── */}
          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-s3">
              <h2 className="text-heading">공정 Route</h2>
              <Button variant="secondary" size="sm" onClick={() => setRouteOpen(true)}>
                Route 편집
              </Button>
            </div>
            <p className="mt-s3 text-label" style={{ color: "var(--text-secondary)" }}>
              {item.route.length === 0
                ? "공정 Route 를 먼저 구성하세요."
                : item.route
                    .map((s) => (s.equipment ? `${s.name} (${s.equipment})` : s.name))
                    .join(" > ")}
            </p>
          </Card>

          {/* ── 생산계획 (§9) ────────────────────────────────────── */}
          <Card pad="sm">
            <div className="flex flex-wrap items-baseline justify-between gap-s3 px-s2 pb-s3">
              <h2 className="text-heading">생산계획</h2>
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="plus" size={13} />}
                onClick={() => setPlanOpen(true)}
                disabled={item.route.length === 0}
              >
                생산계획 추가
              </Button>
            </div>
            {item.productionPlans.length === 0 ? (
              <p className="px-s2 pb-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
                등록된 생산계획이 없어요.
              </p>
            ) : (
              <TableScroll>
                <Table label="생산계획">
                  <THead sticky={false}>
                    <tr>
                      <Th width={80} num>
                        예정일
                      </Th>
                      <Th>품명</Th>
                      <Th width={90} num>
                        수량
                      </Th>
                      <Th width={90}>공정</Th>
                      <Th width={80}>설비</Th>
                      <Th width={80}>담당자</Th>
                      <Th width={200}>상태</Th>
                    </tr>
                  </THead>
                  <tbody>
                    {item.productionPlans.map((p) => (
                      <Tr key={p.id}>
                        <Td num>{monthDay(p.plannedDate) || EMPTY}</Td>
                        <Td strong>{p.partName}</Td>
                        <Td num>
                          <Num v={p.qty} suffix=" EA" />
                        </Td>
                        <Td>{p.processName || EMPTY}</Td>
                        <Td>{p.equipment || EMPTY}</Td>
                        <Td>{p.staffName || EMPTY}</Td>
                        <Td>
                          <span className="flex flex-wrap items-center gap-s2">
                            {/* 6개 관리항목은 추가만이 아니라 수정도 된다
                                (REQ-PROD-002~006). 같은 다이얼로그를 편집 모드로 쓴다. */}
                            <Button variant="secondary" size="sm" onClick={() => setPlanEdit(p)}>
                              수정
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Icon name="trash" size={12} />}
                              onClick={async () => {
                                try {
                                  await deleteProductionPlan(p.id);
                                  reload();
                                  nav.toast("삭제했어요.");
                                } catch (err) {
                                  nav.toast(
                                    err instanceof Error ? err.message : "삭제하지 못했어요.",
                                    "error"
                                  );
                                }
                              }}
                            >
                              <span className="sr-only">{`${monthDay(p.plannedDate)} 생산계획 삭제`}</span>
                            </Button>
                            {p.isDone ? (
                              <StatusBadge status="done" size="sm" label="생산완료" />
                            ) : (
                              <>
                                <span
                                  className="text-caption"
                                  style={{ color: "var(--text-tertiary)" }}
                                >
                                  생산예정
                                </span>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => {
                                    const next = withProductionPlan(item, p.id, {
                                      isDone: true,
                                      doneDate: item.asOf,
                                    });
                                    void optimistic(
                                      next,
                                      () => completeProductionPlan(p.id),
                                      () => {
                                        nav.toastUndo("생산완료로 저장했어요.", async () => {
                                          await updateProductionPlan(p.id, { isDone: false });
                                          reload();
                                        });
                                      }
                                    );
                                  }}
                                >
                                  생산완료
                                </Button>
                              </>
                            )}
                            {p.prepWarning && (
                              <span
                                className="flex items-center gap-s1 text-micro"
                                style={{ color: "var(--state-warn)" }}
                              >
                                <Icon name="alert" size={11} />
                                {p.prepWarning}
                              </span>
                            )}
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            )}
          </Card>

          {/* ── 도면 · 비고 (§5) ─────────────────────────────────── */}
          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-s3">
              <h2 className="text-heading">도면 · 비고</h2>
              <DrawingUpload
                itemId={item.id}
                count={item.drawings.length}
                enabled={drawingsEnabled && item.drawings.length < MAX_DRAWINGS_PER_ITEM}
                onUploaded={reload}
                onError={(m) => nav.toast(m, "error")}
              />
            </div>
            {!drawingsEnabled ? (
              <p className="mt-s3 text-label" style={{ color: "var(--text-tertiary)" }}>
                파일 저장소가 설정되지 않았습니다. 도면 첨부만 사용할 수 없어요.
              </p>
            ) : item.drawings.length === 0 ? (
              <p className="mt-s3 text-label" style={{ color: "var(--text-tertiary)" }}>
                첨부된 도면이 없어요.
              </p>
            ) : (
              <ul className="mt-s3 flex list-none flex-wrap gap-s4 p-0">
                {item.drawings.map((d) => (
                  <li key={d.key} className="flex items-center gap-s2">
                    <Icon name="paperclip" size={14} />
                    <Button
                      variant="link"
                      size="sm"
                      onClick={async () => {
                        try {
                          window.open(await getDrawingUrl(item.id, d.key), "_blank", "noopener");
                        } catch (err) {
                          nav.toast(
                            err instanceof Error ? err.message : "파일을 열지 못했어요.",
                            "error"
                          );
                        }
                      }}
                    >
                      {d.filename}
                    </Button>
                    <span
                      className="text-micro tabular-nums"
                      data-num=""
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {fileSize(d.size)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Icon name="x" size={12} />}
                      onClick={async () => {
                        try {
                          await deleteDrawing(item.id, d.key);
                          reload();
                          nav.toast("삭제했어요.");
                        } catch (err) {
                          nav.toast(
                            err instanceof Error ? err.message : "삭제하지 못했어요.",
                            "error"
                          );
                        }
                      }}
                    >
                      <span className="sr-only">{`${d.filename} 삭제`}</span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <p
              className="mt-s4 text-body"
              style={{ color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}
            >
              {item.note || "비고가 없어요."}
            </p>
          </Card>

          {/* ── 오버레이 ─────────────────────────────────────────── */}
          <DevItemForm
            open={editOpen}
            onClose={() => setEditOpen(false)}
            customers={customers}
            staff={staff}
            processes={processes}
            edit={item}
            onSaved={(saved, message) => {
              setItem(saved);
              nav.toast(message);
            }}
            onError={(m) => nav.toast(m, "error")}
          />

          <IssueForm
            target={issueTarget}
            onClose={() => setIssueTarget(null)}
            onSaved={(m) => {
              nav.toast(m);
              reload();
            }}
            onError={(m) => nav.toast(m, "error")}
          />

          {/* 완료된 항목을 덮어쓰지는 않지만, 손으로 조정한 계획일을 지운다.
              되돌릴 수 없으므로 여기만 확인 다이얼로그를 쓴다. */}
          <ConfirmDialog
            open={regenOpen}
            title="일정을 다시 만들까요?"
            message="완료된 항목은 변경되지 않습니다. 직접 조정한 계획일은 표준 리드타임 값으로 덮어써져요."
            confirmLabel="다시 만들기"
            cancelLabel="취소"
            busy={busy}
            onCancel={() => setRegenOpen(false)}
            onConfirm={async () => {
              setBusy(true);
              try {
                const r = await autoScheduleDevItem(item.id, "overwrite");
                setItem(r.item);
                setRegenOpen(false);
                nav.toast(`주요 일정 ${r.applied}건을 다시 만들었어요.`);
              } catch (err) {
                nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
              } finally {
                setBusy(false);
              }
            }}
          />

          <ConfirmDialog
            open={deleteOpen}
            title="이 개발품을 삭제할까요?"
            message="이 개발품을 삭제할까요? 삭제하면 되돌릴 수 없어요. 마일스톤 · 준비현황 · 생산계획 · 이슈가 함께 지워져요."
            confirmLabel="삭제하기"
            cancelLabel="취소"
            busy={busy}
            onCancel={() => setDeleteOpen(false)}
            onConfirm={async () => {
              setBusy(true);
              try {
                await deleteDevItem(item.id);
                nav.toast("삭제했어요.");
                onBack();
              } catch (err) {
                nav.toast(err instanceof Error ? err.message : "삭제하지 못했어요.", "error");
              } finally {
                setBusy(false);
              }
            }}
          />

          <RouteDialog
            open={routeOpen}
            onClose={() => setRouteOpen(false)}
            processes={processes}
            current={item.route}
            onSave={async (steps) => {
              try {
                await putRoute(item.id, steps);
                setRouteOpen(false);
                reload();
                nav.toast("저장했어요.");
              } catch (err) {
                nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
              }
            }}
          />

          <PrepDialog
            open={prepOpen}
            onClose={() => setPrepOpen(false)}
            existing={item.prepItems.map((p) => p.kind)}
            onSave={async (input) => {
              try {
                await createPrepItem(item.id, input);
                setPrepOpen(false);
                reload();
                nav.toast("저장했어요.");
              } catch (err) {
                nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
              }
            }}
          />

          <PlanDialog
            open={planOpen || planEdit !== null}
            edit={planEdit}
            onClose={() => {
              setPlanOpen(false);
              setPlanEdit(null);
            }}
            route={item.route}
            staff={staff}
            defaultStaffId={item.staffId}
            onSave={async (input) => {
              try {
                if (planEdit) await updateProductionPlan(planEdit.id, input);
                else await createProductionPlan({ ...input, devItemId: item.id });
                setPlanOpen(false);
                setPlanEdit(null);
                reload();
                nav.toast("저장했어요.");
              } catch (err) {
                nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
              }
            }}
          />
        </div>
      )}
    </DataBoundary>
  );
};

/* ── 로딩 스켈레톤: 최종 레이아웃과 같은 모양으로 기다린다 ───────── */

const DetailSkeleton = () => (
  <div className="flex flex-col gap-s5">
    <Skeleton h={30} w="14rem" />
    <div className="grid grid-cols-1 gap-s3 lg:grid-cols-3">
      {Array.from({ length: 3 }, (_, i) => (
        <Skeleton key={i} h={96} />
      ))}
    </div>
    <Skeleton h={110} />
    <Skeleton h={330} />
    <Skeleton h={220} />
  </div>
);

/* ── Route 편집 (§7) ────────────────────────────────────────────── */

/** 순서는 드래그가 아니라 위/아래 버튼이다. 키보드로 동작한다(디자인 가이드 §7). */
const RouteDialog = ({
  open,
  onClose,
  processes,
  current,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  processes: readonly ProcessMaster[];
  current: readonly { processId: string; equipment: string }[];
  onSave: (steps: { processId: string; equipment?: string }[]) => Promise<void>;
}) => {
  const [steps, setSteps] = useState<{ processId: string; equipment: string }[]>([]);
  const [seed, setSeed] = useState("");
  const wanted = `${open}:${current.map((s) => s.processId).join(",")}`;
  if (seed !== wanted) {
    setSeed(wanted);
    setSteps(current.map((s) => ({ processId: s.processId, equipment: s.equipment })));
  }

  const nameOf = (id: string) => processes.find((p) => p.id === id)?.name ?? id;
  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="공정 Route 편집"
      width={560}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" size="md" onClick={() => void onSave(steps)}>
            저장
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-s4">
        <div className="flex flex-col gap-s2">
          <p className="text-caption font-semibold" style={{ color: "var(--text-secondary)" }}>
            사용할 공정
          </p>
          {processes
            .filter((p) => p.active || steps.some((s) => s.processId === p.id))
            .map((p) => (
              <Checkbox
                key={p.id}
                checked={steps.some((s) => s.processId === p.id)}
                label={p.name}
                onChange={(on) =>
                  setSteps((prev) =>
                    on
                      ? [...prev, { processId: p.id, equipment: "" }]
                      : prev.filter((s) => s.processId !== p.id)
                  )
                }
              />
            ))}
        </div>

        {steps.length > 0 && (
          <div
            className="flex flex-col gap-s2 pt-s3"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
          >
            <p className="text-caption font-semibold" style={{ color: "var(--text-secondary)" }}>
              공정 순서와 설비
            </p>
            {steps.map((s, i) => (
              <div key={s.processId} className="flex items-center gap-s2">
                <span className="w-[6rem] text-label">{nameOf(s.processId)}</span>
                <span className="w-[8rem]">
                  <TextInput
                    value={s.equipment}
                    ariaLabel={`${nameOf(s.processId)} 설비`}
                    placeholder="설비"
                    onChange={(v) =>
                      setSteps((prev) =>
                        prev.map((x, xi) => (xi === i ? { ...x, equipment: v } : x))
                      )
                    }
                  />
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={i === 0}
                  icon={<Icon name="arrowUp" size={13} />}
                  onClick={() => move(i, -1)}
                >
                  <span className="sr-only">위로</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={i === steps.length - 1}
                  icon={<Icon name="arrowDown" size={13} />}
                  onClick={() => move(i, 1)}
                >
                  <span className="sr-only">아래로</span>
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

/* ── 준비항목 추가 (§8) ─────────────────────────────────────────── */

const PrepDialog = ({
  open,
  onClose,
  existing,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  existing: readonly string[];
  onSave: (input: {
    kind: string;
    label?: string;
    requiredDate?: string;
    expectedDate?: string;
  }) => Promise<void>;
}) => {
  const [kind, setKind] = useState("jig");
  const [label, setLabel] = useState("");
  const [requiredDate, setRequiredDate] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [error, setError] = useState("");
  const [seed, setSeed] = useState(false);

  if (seed !== open) {
    setSeed(open);
    setKind("jig");
    setLabel("");
    setRequiredDate("");
    setExpectedDate("");
    setError("");
  }

  const kinds = [
    ...PREP_DEFS.map((d) => ({ value: d.kind as string, label: d.label })),
    { value: "jig", label: "치공구" },
    { value: "other", label: "기타" },
  ].filter((k) => k.value === "other" || !existing.includes(k.value));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="준비항목 추가"
      width={480}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            취소
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              if (kind === "other" && !label.trim()) {
                setError("항목명을 입력해 주세요.");
                return;
              }
              void onSave({ kind, label: label.trim(), requiredDate, expectedDate });
            }}
          >
            등록하기
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-s4">
        <Field
          label="종류"
          required
          hint="치공구는 금형과 함께 「생산예정이나 금형 미입고」 경고에 포함돼요."
        >
          {(p) => <Select {...p} value={kind} onChange={setKind} options={kinds} />}
        </Field>
        <Field label="항목명" required={kind === "other"} error={error}>
          {(p) => <TextInput {...p} value={label} onChange={setLabel} maxLength={40} />}
        </Field>
        <Field label="필요일">
          {(p) => <DateField {...p} value={requiredDate} onChange={setRequiredDate} />}
        </Field>
        <Field label="예정일">
          {(p) => <DateField {...p} value={expectedDate} onChange={setExpectedDate} />}
        </Field>
      </div>
    </Dialog>
  );
};

/* ── 생산계획 추가 (§9) ─────────────────────────────────────────── */

const PlanDialog = ({
  open,
  edit,
  onClose,
  route,
  staff,
  defaultStaffId,
  onSave,
}: {
  open: boolean;
  /** 있으면 수정 모드 */
  edit?: ProductionPlan | null;
  onClose: () => void;
  route: readonly { processId: string; name: string; equipment: string }[];
  staff: readonly Staff[];
  defaultStaffId: string;
  onSave: (input: {
    plannedDate: string;
    qty: number;
    processId: string;
    equipment: string;
    staffId: string;
  }) => Promise<void>;
}) => {
  const [plannedDate, setPlannedDate] = useState("");
  const [qty, setQty] = useState("");
  const [processId, setProcessId] = useState("");
  const [equipment, setEquipment] = useState("");
  const [staffId, setStaffId] = useState(defaultStaffId);
  const [error, setError] = useState("");
  const [seed, setSeed] = useState("");

  // 다이얼로그를 다시 열 때 이전 입력이 남아 있으면 안 된다.
  const wanted = `${open}:${edit?.id ?? "new"}`;
  if (seed !== wanted) {
    setSeed(wanted);
    setPlannedDate(edit?.plannedDate ?? "");
    setQty(edit && edit.qty > 0 ? String(edit.qty) : "");
    setProcessId(edit?.processId ?? "");
    setEquipment(edit?.equipment ?? "");
    setStaffId(edit?.staffId || defaultStaffId);
    setError("");
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={edit ? "생산계획 수정" : "생산계획 추가"}
      width={520}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            취소
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              if (!plannedDate) {
                setError("필수 항목을 입력해 주세요.");
                return;
              }
              void onSave({
                plannedDate,
                qty: qty === "" ? 0 : Number.parseInt(qty, 10),
                processId,
                equipment,
                staffId,
              });
            }}
          >
            {edit ? "저장" : "등록하기"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-s4 sm:grid-cols-2">
        <Field label="생산 예정일" required error={error}>
          {(p) => <DateField {...p} value={plannedDate} onChange={setPlannedDate} />}
        </Field>
        <Field label="생산 수량">
          {(p) => <TextInput {...p} type="number" align="right" value={qty} onChange={setQty} />}
        </Field>
        <Field label="공정" hint="공정 Route 안에서만 고를 수 있어요.">
          {(p) => (
            <Select
              {...p}
              value={processId}
              onChange={(v) => {
                setProcessId(v);
                setEquipment(route.find((s) => s.processId === v)?.equipment ?? "");
              }}
              options={route.map((s) => ({ value: s.processId, label: s.name }))}
              placeholder="선택"
            />
          )}
        </Field>
        <Field label="생산설비">
          {(p) => <TextInput {...p} value={equipment} onChange={setEquipment} maxLength={40} />}
        </Field>
        <Field label="담당자" className="sm:col-span-2">
          {(p) => (
            <Select
              {...p}
              value={staffId}
              onChange={setStaffId}
              options={staff.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name }))}
              placeholder="개발품 담당자와 동일"
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
};

/* ── 도면 첨부 ──────────────────────────────────────────────────── */

/** REQ-ITEM-010 의 세 제약. 서버 강제의 사전 검증이다. */
function validateDrawing(file: File, existing: number): string | null {
  if (existing >= MAX_DRAWINGS_PER_ITEM)
    return `도면은 개당 ${MAX_DRAWINGS_PER_ITEM}개까지 첨부할 수 있어요.`;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!DRAWING_EXTENSIONS.includes(ext))
    return `${DRAWING_EXTENSIONS.join(", ")} 파일만 첨부할 수 있어요.`;
  if (file.size > MAX_DRAWING_BYTES)
    return `${MAX_DRAWING_BYTES / 1024 / 1024}MB 를 넘는 파일은 첨부할 수 없어요.`;
  return null;
}

const DrawingUpload = ({
  itemId,
  enabled,
  count,
  onUploaded,
  onError,
}: {
  itemId: string;
  enabled: boolean;
  /** 이미 붙어 있는 도면 수. 개당 상한 검사에 쓴다. */
  count: number;
  onUploaded: () => void;
  onError: (message: string) => void;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input
        ref={ref}
        type="file"
        hidden
        accept={DRAWING_EXTENSIONS.map((x) => `.${x}`).join(",")}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          // 서버도 같은 규칙을 강제한다. 여기서 먼저 막는 이유는 10MB 를
          // 올려보내고 413 을 받는 왕복을 없애기 위한 것뿐이다.
          const reject = validateDrawing(file, count);
          if (reject) {
            onError(reject);
            return;
          }
          setBusy(true);
          try {
            await uploadDrawing(itemId, file);
            onUploaded();
          } catch (err) {
            onError(err instanceof Error ? err.message : "첨부하지 못했어요.");
          } finally {
            setBusy(false);
          }
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        icon={<Icon name="paperclip" size={13} />}
        disabled={!enabled}
        loading={busy}
        onClick={() => ref.current?.click()}
      >
        도면 첨부
      </Button>
    </>
  );
};
